/**
 * Decides which external single-order tabs (Drugs, Business Office, Documents, Audit)
 * should appear based on auth-on-file content, and stores the default tab in {@link UserStore}.
 *
 * @module singleOrderForExternal/utils/tabSwitchElementsGenerator
 */
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { isExceptionFlowForRadiologyOrg } from "../../../enums/organizationTypes";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../store/UserGeneralInfoStore";

/**
 * @param authOnFileDetails - Auth-on-file data whose entries are inspected
 * @returns `true` when any entry contains at least one J-code
 */
export const determineIfDrugTabShouldBeShown = (
  authOnFileDetails: AuthOnFile,
) => {
  const authEntries = authOnFileDetails?.entries;
  if (authEntries && authEntries.length > 0) {
    const allJcodes = authEntries.map((entry) => entry.j_codes).flat();
    return allJcodes.length > 0;
  }
  return false;
};

/**
 * @param authOnFileDetails - Auth-on-file data whose `reports` array is inspected
 * @returns `true` when at least one BO report row exists
 */
export const determineIfBusniessOfficeTabShouldBeShown = (
  authOnFileDetails: AuthOnFile,
) => {
  const reports = authOnFileDetails?.reports;
  if (reports && reports.length > 0) {
    return reports.length > 0;
  }
  return false;
};

/**
 * @param authOnFileDetails - Auth-on-file data whose J-code `documents` arrays are inspected
 * @returns `true` when any J-code has at least one attached document
 */
export const determineIfDocumentTabShouldBeShown = (
  authOnFileDetails: AuthOnFile,
) => {
  const ifJCodeIsAvailable = determineIfDrugTabShouldBeShown(authOnFileDetails);

  if (ifJCodeIsAvailable) {
    const allJCodes = authOnFileDetails?.entries
      ?.map((entry) => entry.j_codes)
      .filter((jCode) => jCode && jCode?.length > 0)
      .flat();
    const docs = allJCodes?.map((jCode) => jCode?.documents).flat();
    return docs && docs?.length > 0 && docs?.some((doc) => doc !== undefined);
  }
  return false;
};

/**
 * Builds the ordered tab list, persists the first tab id to {@link UserStore}, and returns tabs.
 *
 * @param authOnFileDetails - Auth-on-file data used for tab visibility rules
 * @param category - Healthcare facility `category` used for Scan/Instructions labels
 * @returns Side-nav descriptors (`id` matches nested route segments); always ends with audit trail
 */
export const determineFinalTabsListToBeShown = (
  authOnFileDetails: AuthOnFile,
  category?: string | null,
) => {
  const tabSwitchElements: { id: string; label: string }[] = [];
  const ifDrugTabShouldBeShown =
    determineIfDrugTabShouldBeShown(authOnFileDetails);
  const ifBusniessOfficeTabShouldBeShown =
    determineIfBusniessOfficeTabShouldBeShown(authOnFileDetails);
  const ifDocumentTabShouldBeShown =
    determineIfDocumentTabShouldBeShown(authOnFileDetails);

  if (ifDrugTabShouldBeShown) {
    tabSwitchElements.push({
      id: "drugs",
      label: isExceptionFlowForRadiologyOrg(category) ? "Scan" : "Drugs",
    });
  }
  if (ifBusniessOfficeTabShouldBeShown) {
    tabSwitchElements.push({
      id: "businessOffice",
      label: isExceptionFlowForRadiologyOrg(category)
        ? "Instructions"
        : "Business Office",
    });
  }
  if (ifDocumentTabShouldBeShown) {
    tabSwitchElements.push({ id: "documents", label: "Documents" });
  }
  tabSwitchElements.push({ id: "auditTrial", label: "Audit Trail" });
  UserStore.setItem(
    UserGeneralInfoContextKeys.INDEX_TAB,
    tabSwitchElements[0].id,
  );
  return tabSwitchElements;
};
